import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Redirect, Route, Switch} from 'react-router-dom';

import SearchBar from '../components/SearchBar';
import appPage from '../hoc/appPage';
import NoMatch from './NoMatch';
import TimesheetPage from '../../timesheet/pages/TimesheetPage';
import ApprovalPage from '../../approval/pages/ApprovalPage';
import NCRFormPage from '../../ncr/pages/NCRFormPage';
import QualityIssueThankYouPage from '../../ncr/pages/QualityIssueThankYouPage';
import FatalErrorDialog from '../../shared/components/FatalErrorDialog';
import NetworkErrorDialog from '../../shared/components/NetworkErrorDialog';

const LAST_NON_QUALITY_ROUTE_KEY = 'bedrock.lastNonQualityRoute';
const LOCKED_QUERY_KEY = 'locked';

const isLockedSearch = search => {
  try {
    const value = new URLSearchParams(search || '').get(LOCKED_QUERY_KEY);
    return value === '1' || value === 'true';
  } catch (e) {
    return false;
  }
};

const buildLockedSearch = search => {
  try {
    const params = new URLSearchParams(search || '');
    params.set(LOCKED_QUERY_KEY, '1');
    return `?${params.toString()}`;
  } catch (e) {
    return `?${LOCKED_QUERY_KEY}=1`;
  }
};

const MainPage = ({
  currentUser,
  fatalError,
  networkError,
  location,
  menuExpanded,
  rootClick,
  toggleMenu
}) => {
  const pathname = location?.pathname || '';
  const search = location?.search || '';
  const locked = isLockedSearch(search);

  useEffect(() => {
    // Persist the last non-quality-issue route so we can return users after form submission.
    if (
      pathname &&
      !pathname.startsWith('/quality-issue-form') &&
      pathname !== '/login' &&
      pathname !== '/logout'
    ) {
      try {
        sessionStorage.setItem(LAST_NON_QUALITY_ROUTE_KEY, `${pathname}${search}`);
      } catch (e) {
        // ignore
      }
    }
  }, [location]);

  useEffect(() => {
    if (locked && menuExpanded) {
      toggleMenu();
    }
  }, [locked, menuExpanded, toggleMenu]);

  if (locked && !pathname.startsWith('/quality-issue-form')) {
    return <Redirect to={`/quality-issue-form/ncr${buildLockedSearch(search)}`} />;
  }

  return (
    <div className={`main-page ${menuExpanded ? 'menu-open' : ''}`}>
      {!locked && menuExpanded && (
        <div className="nav-backdrop active" onClick={toggleMenu} />
      )}
      {!locked && (
        <SearchBar
          isUserManager={currentUser.ApprovalsView}
          company={currentUser.Company}
          location={location}
          menuExpanded={menuExpanded}
          toggleMenu={toggleMenu}
          username={currentUser.Name}
          currentUser={currentUser}
        />
      )}
      {fatalError && <FatalErrorDialog message={fatalError} />}
      {!fatalError && networkError && <NetworkErrorDialog message={networkError} />}
      <main className="content" onClick={rootClick}>
        <Switch>
          <Route path="/approval/:user/:date/:view" component={ApprovalPage} />
          <Route path="/approval" component={ApprovalPage} />
          <Route path="/timesheet/:date" component={TimesheetPage} />
          <Route path="/timesheet" component={TimesheetPage} />
          <Route path="/quality-issue-form/thank-you" component={QualityIssueThankYouPage} />
          <Route path="/quality-issue-form/:tab" component={NCRFormPage} />
          <Route path="/quality-issue-form" component={NCRFormPage} />
          <Route component={NoMatch} />
        </Switch>
      </main>
    </div>
  );
};

MainPage.propTypes = {
  currentUser: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  rootClick: PropTypes.func.isRequired,
  fatalError: PropTypes.string.isRequired,
  networkError: PropTypes.string
};

export default appPage(MainPage);
